import { memo, useEffect, useRef, useState } from 'react';
import VideoChat, { IncomingVideoCallModal } from '../common/VideoChat';
import PeerCallHooks from '../../utils/peerCall/PeerCallHooks';
import Draggable from 'react-draggable';
import { createPortal } from 'react-dom';
import ROUTES from '../../constants/routes.js';
import useEventListener from 'use-event-listeners';

// Custom hook to get the current pathname without useLocation
const useCurrentPathname = () => {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const handleLocationChange = () => {
      setPathname(window.location.pathname);
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);

    // Create an observer to detect navigation changes
    const observer = new MutationObserver(() => {
      if (pathname !== window.location.pathname) {
        setPathname(window.location.pathname);
      }
    });

    // Observe changes to the document body
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      observer.disconnect();
    };
  }, [pathname]);

  return pathname;
};

const LiveVideo = memo(() => {
  const [div, setDiv] = useState(null);
  const mainDivRef = useRef(null);
  const pathname = useCurrentPathname();

  const conversation_id = PeerCallHooks.useConversationId();
  const { isIncoming, active } = PeerCallHooks.useIsActive();

  useEffect(() => {
    if (active && conversation_id) {
      const actions = document.querySelector('#video-call-actions');
      const vCont = document.querySelector('#video-call-container');

      // @ts-expect-error
      const mouseover = () => actions && (actions.style.display = 'flex');

      // @ts-expect-error
      const mouseleave = () => actions && (actions.style.display = 'none');

      vCont?.addEventListener('mouseover', mouseover);

      vCont?.addEventListener('mouseleave', mouseleave);

      return () => {
        vCont?.removeEventListener('mouseover', mouseover);
        vCont?.removeEventListener('mouseleave', mouseleave);
      };
    }
  }, [active, conversation_id, div]);

  useEventListener({ listeners: { 'conversation-mounted': (div) => setDiv(div) } });

  PeerCallHooks.useSocketListeners();
  PeerCallHooks.usePageUnload();

  const nodeRef = useRef<HTMLDivElement>(null);

  const isOnConversationPage = pathname === ROUTES.CONVERSATIONS.pathname;
  const callContainerNode = div && isOnConversationPage ? div : mainDivRef.current;

  useEffect(() => {
    !isOnConversationPage && setDiv(null);
  }, [isOnConversationPage]);

  return (
    <div ref={mainDivRef}>
      {callContainerNode &&
        createPortal(
          <div className={isOnConversationPage ? undefined : 'fixed bottom-4 right-4 w-[450px]'}>
            <Draggable nodeRef={nodeRef}>
              <div ref={nodeRef}>
                {active && <VideoChat conversation_id={conversation_id} />}
                <IncomingVideoCallModal isIncoming={isIncoming} />
              </div>
            </Draggable>
          </div>,
          callContainerNode
        )}
    </div>
  );
});

export default LiveVideo;
